import React, { useState, useEffect } from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement, PointElement, ArcElement, Title, Tooltip, Legend } from 'chart.js';
import { Bar, Line, Pie } from 'react-chartjs-2';
import api from '../utils/api';
import { useLang } from '../context/LanguageContext';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, ArcElement, Title, Tooltip, Legend);

const FRUIT_COLORS = {
  Orange: '#f97316', Lemon: '#facc15', Grapefruit: '#fb923c', Mandarin: '#ea580c',
  Lime: '#84cc16', Tangerine: '#f59e0b', Clementine: '#fb7185', Kumquat: '#fbbf24'
};

function getDateRange(period) {
  const now = new Date();
  const to = now.toISOString().slice(0, 10);
  let from;
  if (period === '1D') {
    from = to; // today only
  } else if (period === '1W') {
    const d = new Date(now);
    d.setDate(d.getDate() - 7);
    from = d.toISOString().slice(0, 10);
  } else if (period === '1M') {
    const d = new Date(now);
    d.setMonth(d.getMonth() - 1);
    from = d.toISOString().slice(0, 10);
  }
  return { date_from: from, date_to: to };
}

export default function Reports() {
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inspectors, setInspectors] = useState([]);
  const [filters, setFilters] = useState({ inspector_id: '', date_from: '', date_to: '' });
  const [activePeriod, setActivePeriod] = useState(null);
  const [selectedPacker, setSelectedPacker] = useState('');
  const [packerHistory, setPackerHistory] = useState(null);
  const [packerHistoryLoading, setPackerHistoryLoading] = useState(false);
  const [packerSearch, setPackerSearch] = useState('');
  const [selectedFruit, setSelectedFruit] = useState('');
  const [packagerPage, setPackagerPage] = useState(0);
  const [activeSection, setActiveSection] = useState('overview');
  const [packagerMinBoxes, setPackagerMinBoxes] = useState('');
  const [packagerRatingFilter, setPackagerRatingFilter] = useState('');
  const [packagerSort, setPackagerSort] = useState('total_asc');
  const PACKAGER_PER_PAGE = 10;

  useEffect(() => {
    api.get('/admin/inspectors').then(res => setInspectors(res.data)).catch(() => {});
    fetchReport();
  }, []);

  const fetchReport = async (f = filters, productId = selectedFruit) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (f.inspector_id) params.append('inspector_id', f.inspector_id);
      if (f.date_from) params.append('date_from', f.date_from);
      if (f.date_to) params.append('date_to', f.date_to);
      if (productId) params.append('fruit_type', productId);
      const res = await api.get(`/reports/summary?${params}`);
      setData(res.data);
      if (selectedPacker) fetchPackerHistory(selectedPacker, f);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleFilter = () => { setActivePeriod(null); setPackagerPage(0); fetchReport(); };

  const handleQuickPeriod = (period) => {
    const range = getDateRange(period);
    const newFilters = { ...filters, ...range };
    setFilters(newFilters);
    setActivePeriod(period);
    fetchReport(newFilters);
  };

  const handleClearFilters = () => {
    const cleared = { inspector_id: '', date_from: '', date_to: '' };
    setFilters(cleared);
    setActivePeriod(null);
    setSelectedFruit('');
    fetchReport(cleared, '');
  };

  const handleFruitChange = (fruitType) => {
    setSelectedFruit(fruitType);
    fetchReport(filters, fruitType);
  };

  const fetchPackerHistory = async (packager_number, f = filters) => {
    if (!packager_number) { setPackerHistory(null); return; }
    setPackerHistoryLoading(true);
    try {
      const params = new URLSearchParams({ packager_number });
      if (f.date_from) params.append('date_from', f.date_from);
      if (f.date_to) params.append('date_to', f.date_to);
      const res = await api.get(`/reports/packer-history?${params}`);
      setPackerHistory(res.data);
    } catch (err) { console.error(err); }
    finally { setPackerHistoryLoading(false); }
  };

  const handlePackerSelect = (packager_number) => {
    setSelectedPacker(packager_number);
    fetchPackerHistory(packager_number);
  };

  const exportData = async (type) => {
    try {
      const params = new URLSearchParams();
      if (filters.inspector_id) params.append('inspector_id', filters.inspector_id);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      const res = await api.get(`/reports/export/${type}?${params}`, { responseType: 'blob' });
      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = type === 'csv' ? 'inspections_report.csv' : 'inspections_report.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export error:', err);
    }
  };

  // Determine top/bottom performers
  const topPackager = data && data.packagerStats.length > 0
    ? [...data.packagerStats].sort((a, b) => parseFloat(a.avg_total_pct) - parseFloat(b.avg_total_pct))[0]
    : null;

  const worstPackager = data && data.packagerStats.length > 1
    ? [...data.packagerStats].sort((a, b) => parseFloat(b.avg_total_pct) - parseFloat(a.avg_total_pct))[0]
    : null;

  const topInspector = data && data.inspectorStats.length > 0
    ? [...data.inspectorStats].sort((a, b) => b.boxes_inspected - a.boxes_inspected)[0]
    : null;

  const worstInspector = data && data.inspectorStats.length > 1
    ? [...data.inspectorStats].sort((a, b) => a.boxes_inspected - b.boxes_inspected)[0]
    : null;

  const inspectorBarData = data ? {
    labels: data.inspectorStats.map(s => s.username),
    datasets: [{ label: t('boxes'), data: data.inspectorStats.map(s => s.boxes_inspected),
      backgroundColor: 'rgba(249, 115, 22, 0.7)', borderColor: '#f97316', borderWidth: 2, borderRadius: 8 }]
  } : null;

  const dailyLineData = data ? {
    labels: data.dailyTrend.map(d => new Date(d.date).toLocaleDateString()),
    datasets: [
      { label: t('minor_pct'), data: data.dailyTrend.map(d => parseFloat(d.avg_minor_pct).toFixed(2)),
        borderColor: '#facc15', backgroundColor: 'rgba(250, 204, 21, 0.1)', fill: true, tension: 0.4, pointBackgroundColor: '#facc15' },
      { label: t('major_pct'), data: data.dailyTrend.map(d => parseFloat(d.avg_major_pct).toFixed(2)),
        borderColor: '#ef4444', backgroundColor: 'rgba(239, 68, 68, 0.1)', fill: true, tension: 0.4, pointBackgroundColor: '#ef4444' },
      { label: t('critical_pct'), data: data.dailyTrend.map(d => parseFloat(d.avg_critical_pct).toFixed(2)),
        borderColor: '#7c3aed', backgroundColor: 'rgba(124, 58, 237, 0.1)', fill: true, tension: 0.4, pointBackgroundColor: '#7c3aed' },
      { label: t('boxes'), data: data.dailyTrend.map(d => d.boxes_inspected),
        borderColor: '#84cc16', backgroundColor: 'rgba(132, 204, 22, 0.1)', fill: false, tension: 0.4, pointBackgroundColor: '#84cc16', yAxisID: 'y1' }
    ]
  } : null;

  const packerLineData = packerHistory && packerHistory.dailyHistory.length > 0 ? {
    labels: packerHistory.dailyHistory.map(d => new Date(d.date).toLocaleDateString()),
    datasets: [
      { label: t('minor_pct'), data: packerHistory.dailyHistory.map(d => parseFloat(d.avg_minor_pct).toFixed(2)),
        borderColor: '#facc15', backgroundColor: 'rgba(250, 204, 21, 0.1)', fill: true, tension: 0.4, pointBackgroundColor: '#facc15' },
      { label: t('major_pct'), data: packerHistory.dailyHistory.map(d => parseFloat(d.avg_major_pct).toFixed(2)),
        borderColor: '#ef4444', backgroundColor: 'rgba(239, 68, 68, 0.1)', fill: true, tension: 0.4, pointBackgroundColor: '#ef4444' },
      { label: t('critical_pct'), data: packerHistory.dailyHistory.map(d => parseFloat(d.avg_critical_pct).toFixed(2)),
        borderColor: '#7c3aed', backgroundColor: 'rgba(124, 58, 237, 0.1)', fill: true, tension: 0.4, pointBackgroundColor: '#7c3aed' },
      { label: t('total_pct'), data: packerHistory.dailyHistory.map(d => parseFloat(d.avg_total_pct).toFixed(2)),
        borderColor: '#f97316', backgroundColor: 'rgba(249, 115, 22, 0.08)', fill: false, tension: 0.4, pointBackgroundColor: '#f97316', borderDash: [5, 3] },
      { label: t('boxes'), data: packerHistory.dailyHistory.map(d => d.boxes_inspected),
        borderColor: '#84cc16', backgroundColor: 'rgba(132, 204, 22, 0.1)', fill: false, tension: 0.4, pointBackgroundColor: '#84cc16', yAxisID: 'y1' }
    ]
  } : null;

  const filteredPackagerStats = data ? data.packagerStats
    .filter(p => {
      if (packagerMinBoxes && p.boxes_inspected < parseInt(packagerMinBoxes)) return false;
      if (packagerRatingFilter) {
        const avgCritical = parseFloat(p.avg_critical_pct);
        const avgTotal = parseFloat(p.avg_total_pct);
        const rating = avgCritical > 1 ? 'critical' : avgTotal <= 3 ? 'star' : avgTotal <= 5 ? 'good' : avgTotal <= 10 ? 'warning' : 'alert';
        if (packagerRatingFilter !== rating) return false;
      }
      return true;
    })
    .sort((a, b) => {
      switch (packagerSort) {
        case 'total_asc': return parseFloat(a.avg_total_pct) - parseFloat(b.avg_total_pct);
        case 'total_desc': return parseFloat(b.avg_total_pct) - parseFloat(a.avg_total_pct);
        case 'boxes_desc': return b.boxes_inspected - a.boxes_inspected;
        case 'boxes_asc': return a.boxes_inspected - b.boxes_inspected;
        case 'name_asc': return (a.packager_name || '').localeCompare(b.packager_name || '');
        default: return 0;
      }
    }) : [];

  const fruitPieData = data ? {
    labels: data.fruitTypeStats.map(f => f.fruit_type),
    datasets: [{ label: t('boxes'), data: data.fruitTypeStats.map(f => f.boxes_inspected),
      backgroundColor: data.fruitTypeStats.map(f => FRUIT_COLORS[f.fruit_type] || '#94a3b8'), borderWidth: 3, borderColor: '#fff' }]
  } : null;

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(180deg, #fff7ed 0%, #f8fafc 100%)' }}>
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6 fade-in">
          <h1 className="text-2xl md:text-3xl font-extrabold text-gray-800">📊 {t('reports_title')}</h1>
          <div className="flex space-x-2">
            <button onClick={() => exportData('csv')} className="px-5 py-3 bg-green-500 text-white rounded-xl font-semibold active:bg-green-600 text-sm shadow-md touch-active">
              📥 {t('export_csv')}
            </button>
            <button onClick={() => exportData('pdf')} className="px-5 py-3 bg-red-500 text-white rounded-xl font-semibold active:bg-red-600 text-sm shadow-md touch-active">
              📄 {t('export_pdf')}
            </button>
          </div>
        </div>

        {/* Quick Time Buttons + Filters */}
        <div className="bg-white rounded-2xl shadow-md p-5 border border-orange-100 mb-6 fade-in">
          {/* Quick period buttons */}
          <div className="flex flex-wrap gap-2 mb-4">
            {[
              { key: '1D', label: t('period_1d') },
              { key: '1W', label: t('period_1w') },
              { key: '1M', label: t('period_1m') }
            ].map(p => (
              <button key={p.key} onClick={() => handleQuickPeriod(p.key)}
                className={`px-5 py-3 rounded-xl font-bold text-sm transition-all touch-active ${
                  activePeriod === p.key
                    ? 'bg-orange-500 text-white shadow-md'
                    : 'bg-orange-50 text-orange-700 border border-orange-200 active:bg-orange-100'
                }`}>
                {p.label}
              </button>
            ))}
            <button onClick={handleClearFilters}
              className={`px-5 py-3 rounded-xl font-bold text-sm transition-all touch-active ${
                !activePeriod && !filters.date_from && !filters.date_to
                  ? 'bg-orange-500 text-white shadow-md'
                  : 'bg-gray-50 text-gray-600 border border-gray-200 active:bg-gray-100'
              }`}>
              {t('all_time')}
            </button>
          </div>

          {/* Detailed filters */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
            <div>
              <label className="block text-xs font-bold text-gray-600 mb-1">{t('inspector')}</label>
              <select value={filters.inspector_id} onChange={(e) => setFilters({ ...filters, inspector_id: e.target.value })}
                className="w-full px-4 py-3 border-2 border-orange-200 rounded-xl text-sm focus:outline-none focus:border-orange-400 bg-white">
                <option value="">{t('all')} {t('inspector')}</option>
                {inspectors.map(i => <option key={i.id} value={i.id}>{i.username}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-600 mb-1">{t('from_date')}</label>
              <input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                className="w-full px-4 py-3 border-2 border-orange-200 rounded-xl text-sm focus:outline-none focus:border-orange-400" />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-600 mb-1">{t('to_date')}</label>
              <input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                className="w-full px-4 py-3 border-2 border-orange-200 rounded-xl text-sm focus:outline-none focus:border-orange-400" />
            </div>
            <button onClick={handleFilter}
              className="py-3 px-6 rounded-xl text-white font-bold text-sm shadow-md active:shadow-sm transition-all touch-active"
              style={{ background: 'linear-gradient(135deg, #f97316, #f59e0b)' }}>
              🔍 {t('apply')}
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-20">
            <div className="text-5xl fruit-spin inline-block">🍊</div>
            <p className="mt-4 text-orange-600 font-semibold">{t('crunching')}</p>
          </div>
        ) : data && (
          <>
            {/* Section Navigation */}
            <div className="flex gap-2 mb-6 overflow-x-auto pb-2 hide-scrollbar">
              {[
                { id: 'overview', icon: '📊', label: t('nav_overview') },
                { id: 'charts', icon: '📈', label: t('nav_charts') },
                { id: 'packagers', icon: '📦', label: t('nav_packagers') },
                { id: 'inspectors', icon: '👷', label: t('nav_inspectors') },
              ].map(s => (
                <button key={s.id} onClick={() => { setActiveSection(s.id); document.getElementById(`section-${s.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
                  className={`flex items-center gap-2 px-5 py-3 rounded-xl font-bold text-sm whitespace-nowrap transition-all ${activeSection === s.id ? 'bg-orange-500 text-white shadow-lg' : 'bg-white text-gray-600 border-2 border-orange-100 hover:border-orange-300'}`}>
                  <span>{s.icon}</span> {s.label}
                </button>
              ))}
            </div>

            {/* Top Performers */}
            <div id="section-overview"></div>
            {(topPackager || topInspector) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                {topPackager && (
                  <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-2xl shadow-md p-5 border-2 border-yellow-300 fade-in">
                    <div className="flex items-center gap-4">
                      <div className="text-5xl">🏆</div>
                      <div className="flex-1">
                        <div className="text-xs font-bold text-yellow-600 uppercase tracking-wide">{t('top_packager')}</div>
                        <div className="text-xl font-extrabold text-gray-800 mt-1">{topPackager.packager_number}</div>
                        {topPackager.packager_name !== topPackager.packager_number && (
                          <div className="text-sm text-gray-600 font-semibold">{topPackager.packager_name}</div>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-gray-500">{t('total_pct')}</div>
                        <div className="text-2xl font-extrabold text-green-600">{parseFloat(topPackager.avg_total_pct).toFixed(2)}%</div>
                        <div className="text-xs text-gray-400 mt-1">{topPackager.boxes_inspected} {t('boxes')}</div>
                      </div>
                    </div>
                    <div className="flex gap-3 mt-3">
                      <div className="flex-1 bg-white bg-opacity-60 rounded-xl p-2 text-center">
                        <div className="text-xs text-yellow-600 font-bold">{t('minor_pct')}</div>
                        <div className="font-extrabold text-yellow-700">{parseFloat(topPackager.avg_minor_pct).toFixed(2)}%</div>
                      </div>
                      <div className="flex-1 bg-white bg-opacity-60 rounded-xl p-2 text-center">
                        <div className="text-xs text-red-600 font-bold">{t('major_pct')}</div>
                        <div className="font-extrabold text-red-700">{parseFloat(topPackager.avg_major_pct).toFixed(2)}%</div>
                      </div>
                      <div className="flex-1 bg-white bg-opacity-60 rounded-xl p-2 text-center">
                        <div className="text-xs text-purple-600 font-bold">{t('critical_pct')}</div>
                        <div className="font-extrabold text-purple-700">{parseFloat(topPackager.avg_critical_pct).toFixed(2)}%</div>
                      </div>
                    </div>
                  </div>
                )}
                {topInspector && (
                  <div className="bg-gradient-to-br from-blue-50 to-green-50 rounded-2xl shadow-md p-5 border-2 border-blue-300 fade-in" style={{ animationDelay: '0.1s' }}>
                    <div className="flex items-center gap-4">
                      <div className="text-5xl">⭐</div>
                      <div className="flex-1">
                        <div className="text-xs font-bold text-blue-600 uppercase tracking-wide">{t('top_inspector')}</div>
                        <div className="text-xl font-extrabold text-gray-800 mt-1">{topInspector.username}</div>
                        <div className="text-sm text-gray-600 font-semibold">{t('quality_controller')}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-gray-500">{t('boxes')}</div>
                        <div className="text-2xl font-extrabold text-blue-600">{topInspector.boxes_inspected}</div>
                        <div className="text-xs text-gray-400 mt-1">{t('total_pct')} {parseFloat(topInspector.avg_total_pct).toFixed(2)}%</div>
                      </div>
                    </div>
                    <div className="flex gap-3 mt-3">
                      <div className="flex-1 bg-white bg-opacity-60 rounded-xl p-2 text-center">
                        <div className="text-xs text-yellow-600 font-bold">{t('minor_found')}</div>
                        <div className="font-extrabold text-yellow-700">{topInspector.total_minor}</div>
                      </div>
                      <div className="flex-1 bg-white bg-opacity-60 rounded-xl p-2 text-center">
                        <div className="text-xs text-red-600 font-bold">{t('major_found')}</div>
                        <div className="font-extrabold text-red-700">{topInspector.total_major}</div>
                      </div>
                      <div className="flex-1 bg-white bg-opacity-60 rounded-xl p-2 text-center">
                        <div className="text-xs text-purple-600 font-bold">{t('critical_found')}</div>
                        <div className="font-extrabold text-purple-700">{topInspector.total_critical}</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Worst Performers */}
            {(worstPackager || worstInspector) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {worstPackager && (
                  <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl shadow-md p-5 border-2 border-red-300 fade-in">
                    <div className="flex items-center gap-4">
                      <div className="text-5xl">⚠️</div>
                      <div className="flex-1">
                        <div className="text-xs font-bold text-red-600 uppercase tracking-wide">{t('worst_packager')}</div>
                        <div className="text-xl font-extrabold text-gray-800 mt-1">{worstPackager.packager_number}</div>
                        {worstPackager.packager_name !== worstPackager.packager_number && (
                          <div className="text-sm text-gray-600 font-semibold">{worstPackager.packager_name}</div>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-gray-500">{t('total_pct')}</div>
                        <div className="text-2xl font-extrabold text-red-600">{parseFloat(worstPackager.avg_total_pct).toFixed(2)}%</div>
                        <div className="text-xs text-gray-400 mt-1">{worstPackager.boxes_inspected} {t('boxes')}</div>
                      </div>
                    </div>
                    <div className="flex gap-3 mt-3">
                      <div className="flex-1 bg-white bg-opacity-60 rounded-xl p-2 text-center">
                        <div className="text-xs text-yellow-600 font-bold">{t('minor_pct')}</div>
                        <div className="font-extrabold text-yellow-700">{parseFloat(worstPackager.avg_minor_pct).toFixed(2)}%</div>
                      </div>
                      <div className="flex-1 bg-white bg-opacity-60 rounded-xl p-2 text-center">
                        <div className="text-xs text-red-600 font-bold">{t('major_pct')}</div>
                        <div className="font-extrabold text-red-700">{parseFloat(worstPackager.avg_major_pct).toFixed(2)}%</div>
                      </div>
                      <div className="flex-1 bg-white bg-opacity-60 rounded-xl p-2 text-center">
                        <div className="text-xs text-purple-600 font-bold">{t('critical_pct')}</div>
                        <div className="font-extrabold text-purple-700">{parseFloat(worstPackager.avg_critical_pct).toFixed(2)}%</div>
                      </div>
                    </div>
                  </div>
                )}
                {worstInspector && (
                  <div className="bg-gradient-to-br from-gray-50 to-red-50 rounded-2xl shadow-md p-5 border-2 border-gray-300 fade-in" style={{ animationDelay: '0.1s' }}>
                    <div className="flex items-center gap-4">
                      <div className="text-5xl">📉</div>
                      <div className="flex-1">
                        <div className="text-xs font-bold text-gray-500 uppercase tracking-wide">{t('least_active_inspector')}</div>
                        <div className="text-xl font-extrabold text-gray-800 mt-1">{worstInspector.username}</div>
                        <div className="text-sm text-gray-600 font-semibold">{t('quality_controller')}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-gray-500">{t('boxes')}</div>
                        <div className="text-2xl font-extrabold text-gray-500">{worstInspector.boxes_inspected}</div>
                        <div className="text-xs text-gray-400 mt-1">{t('total_pct')} {parseFloat(worstInspector.avg_total_pct).toFixed(2)}%</div>
                      </div>
                    </div>
                    <div className="flex gap-3 mt-3">
                      <div className="flex-1 bg-white bg-opacity-60 rounded-xl p-2 text-center">
                        <div className="text-xs text-yellow-600 font-bold">{t('minor_found')}</div>
                        <div className="font-extrabold text-yellow-700">{worstInspector.total_minor}</div>
                      </div>
                      <div className="flex-1 bg-white bg-opacity-60 rounded-xl p-2 text-center">
                        <div className="text-xs text-red-600 font-bold">{t('major_found')}</div>
                        <div className="font-extrabold text-red-700">{worstInspector.total_major}</div>
                      </div>
                      <div className="flex-1 bg-white bg-opacity-60 rounded-xl p-2 text-center">
                        <div className="text-xs text-purple-600 font-bold">{t('critical_found')}</div>
                        <div className="font-extrabold text-purple-700">{worstInspector.total_critical}</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Summary Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-6">
              {[
                { label: t('total_boxes'), value: data.metrics.total_boxes, icon: '📦', bg: 'bg-orange-50 border-orange-200' },
                { label: t('avg_minor'), value: parseFloat(data.metrics.avg_minor_defect_percent).toFixed(2) + '%', icon: '⚡', bg: 'bg-yellow-50 border-yellow-200' },
                { label: t('avg_major'), value: parseFloat(data.metrics.avg_major_defect_percent).toFixed(2) + '%', icon: '🚨', bg: 'bg-red-50 border-red-200' },
                { label: t('avg_critical'), value: parseFloat(data.metrics.avg_critical_defect_percent).toFixed(2) + '%', icon: '💀', bg: 'bg-purple-50 border-purple-200' },
                { label: t('total_defects'), value: Number(data.metrics.total_defects).toLocaleString(), icon: '🔢', bg: 'bg-blue-50 border-blue-200' },
                { label: t('fruits_inspected'), value: Number(data.metrics.total_fruits_inspected).toLocaleString(), icon: '🍊', bg: 'bg-green-50 border-green-200' }
              ].map((card, i) => (
                <div key={i} className={`rounded-2xl shadow-sm p-4 border-2 ${card.bg} fade-in`} style={{ animationDelay: `${i * 0.08}s` }}>
                  <div className="text-2xl mb-1">{card.icon}</div>
                  <div className="text-xl md:text-2xl font-extrabold text-gray-800">{card.value}</div>
                  <div className="text-xs font-semibold text-gray-500 mt-1">{card.label}</div>
                </div>
              ))}
            </div>

            {/* Charts */}
            <div id="section-charts"></div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {inspectorBarData && (
                <div className="bg-white rounded-2xl shadow-md p-5 border border-orange-100">
                  <h3 className="text-base font-bold text-gray-800 mb-3">📊 {t('boxes_per_inspector')}</h3>
                  <Bar data={inspectorBarData} options={{ responsive: true, plugins: { legend: { display: false } }, scales: { y: { beginAtZero: true } } }} />
                </div>
              )}
              {fruitPieData && (
                <div className="bg-white rounded-2xl shadow-md p-5 border border-orange-100">
                  <h3 className="text-base font-bold text-gray-800 mb-3">🍊 {t('product_distribution')}</h3>
                  <div className="max-w-xs mx-auto">
                    <Pie data={fruitPieData} options={{ responsive: true, plugins: { legend: { position: 'bottom', labels: { padding: 12, font: { size: 11 } } } } }} />
                  </div>
                </div>
              )}
            </div>

            {dailyLineData && (
              <div className="bg-white rounded-2xl shadow-md p-5 border border-orange-100 mb-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3 gap-2">
                  <h3 className="text-base font-bold text-gray-800">📈 {t('defect_trends')}</h3>
                  <select value={selectedFruit} onChange={(e) => handleFruitChange(e.target.value)}
                    className="px-3 py-2 border-2 border-orange-200 rounded-xl text-sm font-semibold focus:outline-none focus:border-orange-400 bg-white">
                    <option value="">{t('all_products')}</option>
                    {data.fruitTypes && data.fruitTypes.map(f => (
                      <option key={f} value={f}>{f}</option>
                    ))}
                  </select>
                </div>
                <Line data={dailyLineData} options={{
                  responsive: true, interaction: { mode: 'index', intersect: false },
                  scales: {
                    y: { beginAtZero: true, title: { display: true, text: 'Defect %' } },
                    y1: { beginAtZero: true, position: 'right', title: { display: true, text: t('boxes') }, grid: { drawOnChartArea: false } }
                  }
                }} />
              </div>
            )}

            {/* Packager Performance */}
            <div id="section-packagers"></div>
            <div className="bg-white rounded-2xl shadow-md p-5 border border-orange-100 mb-6">
              <h3 className="text-base font-bold text-gray-800 mb-3">📦 {t('packager_performance')}</h3>

              {/* Packager Filters */}
              <div className="flex flex-wrap gap-3 mb-4 p-3 bg-orange-50 rounded-xl border border-orange-100">
                <div className="flex-1 min-w-[120px]">
                  <label className="block text-xs font-bold text-gray-600 mb-1">{t('min_boxes')}</label>
                  <input
                    type="number"
                    min="0"
                    value={packagerMinBoxes}
                    onChange={e => { setPackagerMinBoxes(e.target.value); setPackagerPage(0); }}
                    placeholder="0"
                    className="w-full px-3 py-2 border-2 border-orange-200 rounded-lg text-sm focus:outline-none focus:border-orange-400 bg-white"
                  />
                </div>
                <div className="flex-1 min-w-[140px]">
                  <label className="block text-xs font-bold text-gray-600 mb-1">{t('rating')}</label>
                  <select
                    value={packagerRatingFilter}
                    onChange={e => { setPackagerRatingFilter(e.target.value); setPackagerPage(0); }}
                    className="w-full px-3 py-2 border-2 border-orange-200 rounded-lg text-sm focus:outline-none focus:border-orange-400 bg-white">
                    <option value="">{t('all')} {t('rating')}</option>
                    <option value="star">🌟 {t('rating_star')}</option>
                    <option value="good">👍 {t('rating_good')}</option>
                    <option value="warning">⚠️ {t('rating_warning')}</option>
                    <option value="alert">🚨 {t('rating_alert')}</option>
                    <option value="critical">💀 {t('rating_critical')}</option>
                  </select>
                </div>
                <div className="flex-1 min-w-[160px]">
                  <label className="block text-xs font-bold text-gray-600 mb-1">{t('sort_by')}</label>
                  <select
                    value={packagerSort}
                    onChange={e => { setPackagerSort(e.target.value); setPackagerPage(0); }}
                    className="w-full px-3 py-2 border-2 border-orange-200 rounded-lg text-sm focus:outline-none focus:border-orange-400 bg-white">
                    <option value="total_asc">{t('total_pct')} ↑</option>
                    <option value="total_desc">{t('total_pct')} ↓</option>
                    <option value="boxes_desc">{t('boxes')} ↓</option>
                    <option value="boxes_asc">{t('boxes')} ↑</option>
                    <option value="name_asc">{t('name')} A-Z</option>
                  </select>
                </div>
                {(packagerMinBoxes || packagerRatingFilter) && (
                  <div className="flex items-end">
                    <button onClick={() => { setPackagerMinBoxes(''); setPackagerRatingFilter(''); setPackagerPage(0); }}
                      className="px-4 py-2 rounded-lg text-sm font-bold bg-gray-200 text-gray-600 active:bg-gray-300 transition-all">
                      {t('clear_filters')}
                    </button>
                  </div>
                )}
              </div>

              {filteredPackagerStats.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <div className="text-3xl mb-2">📦</div>
                  <div className="text-sm font-semibold">{t('no_packers_match')}</div>
                </div>
              ) : (
                <>
                  <div className="text-xs text-gray-500 font-semibold mb-2">
                    {t('showing')} {filteredPackagerStats.length} / {data.packagerStats.length} {t('nav_packagers').toLowerCase()}
                  </div>
                  <div className="touch-scroll">
                    <table className="w-full text-sm" style={{ minWidth: '700px' }}>
                      <thead>
                        <tr className="border-b-2 border-orange-100">
                          <th className="text-left py-3 px-3 font-bold text-gray-600">{t('packager')}</th>
                          <th className="text-left py-3 px-3 font-bold text-gray-600">{t('name')}</th>
                          <th className="text-center py-3 px-3 font-bold text-gray-600">{t('boxes')}</th>
                          <th className="text-center py-3 px-3 font-bold text-yellow-600">{t('avg_minor')}</th>
                          <th className="text-center py-3 px-3 font-bold text-red-600">{t('avg_major')}</th>
                          <th className="text-center py-3 px-3 font-bold text-purple-600">{t('avg_critical')}</th>
                          <th className="text-center py-3 px-3 font-bold text-gray-600">{t('total_pct')}</th>
                          <th className="text-center py-3 px-3 font-bold text-gray-600">{t('rating')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredPackagerStats.slice(packagerPage * PACKAGER_PER_PAGE, (packagerPage + 1) * PACKAGER_PER_PAGE).map((p, i) => {
                          const avgMajor = parseFloat(p.avg_major_pct);
                          const avgCritical = parseFloat(p.avg_critical_pct);
                          const avgTotal = parseFloat(p.avg_total_pct);
                          return (
                            <tr key={i} className="border-b border-gray-50 hover:bg-orange-50 transition-colors">
                              <td className="py-3 px-3 font-semibold">{p.packager_number}</td>
                              <td className="py-3 px-3">{p.packager_name}</td>
                              <td className="py-3 px-3 text-center font-bold">{p.boxes_inspected}</td>
                              <td className="py-3 px-3 text-center"><span className="px-2 py-1 rounded-full text-xs font-bold bg-yellow-100 text-yellow-700">{parseFloat(p.avg_minor_pct).toFixed(2)}%</span></td>
                              <td className="py-3 px-3 text-center"><span className={`px-2 py-1 rounded-full text-xs font-bold ${avgMajor > 5 ? 'bg-red-100 text-red-700' : avgMajor > 2 ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'}`}>{avgMajor.toFixed(2)}%</span></td>
                              <td className="py-3 px-3 text-center"><span className={`px-2 py-1 rounded-full text-xs font-bold ${avgCritical > 0 ? 'bg-purple-100 text-purple-700' : 'bg-green-100 text-green-700'}`}>{avgCritical.toFixed(2)}%</span></td>
                              <td className="py-3 px-3 text-center font-bold">{avgTotal.toFixed(2)}%</td>
                              <td className="py-3 px-3 text-center text-lg">{avgCritical > 1 ? '💀' : avgTotal <= 3 ? '🌟' : avgTotal <= 5 ? '👍' : avgTotal <= 10 ? '⚠️' : '🚨'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {filteredPackagerStats.length > PACKAGER_PER_PAGE && (
                    <div className="flex items-center justify-between mt-4 pt-3 border-t border-orange-100">
                      <span className="text-xs text-gray-500 font-semibold">
                        {packagerPage * PACKAGER_PER_PAGE + 1}-{Math.min((packagerPage + 1) * PACKAGER_PER_PAGE, filteredPackagerStats.length)} / {filteredPackagerStats.length}
                      </span>
                      <div className="flex gap-2">
                        <button onClick={() => setPackagerPage(p => Math.max(0, p - 1))} disabled={packagerPage === 0}
                          className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${packagerPage === 0 ? 'bg-gray-100 text-gray-300' : 'bg-orange-100 text-orange-700 hover:bg-orange-200 active:bg-orange-300'}`}>
                          ← {t('prev')}
                        </button>
                        <button onClick={() => setPackagerPage(p => Math.min(Math.ceil(filteredPackagerStats.length / PACKAGER_PER_PAGE) - 1, p + 1))}
                          disabled={(packagerPage + 1) * PACKAGER_PER_PAGE >= filteredPackagerStats.length}
                          className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${(packagerPage + 1) * PACKAGER_PER_PAGE >= filteredPackagerStats.length ? 'bg-gray-100 text-gray-300' : 'bg-orange-100 text-orange-700 hover:bg-orange-200 active:bg-orange-300'}`}>
                          {t('next')} →
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Packer History */}
            <div className="bg-white rounded-2xl shadow-md p-5 border border-orange-100 mb-6">
              <h3 className="text-base font-bold text-gray-800 mb-4">📅 {t('packer_history_title')}</h3>
              <div className="mb-4">
                <div className="relative">
                  <input
                    type="text"
                    placeholder={t('packer_history_prompt')}
                    value={packerSearch}
                    onChange={e => setPackerSearch(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-orange-200 rounded-xl text-sm focus:outline-none focus:border-orange-400 pr-10"
                  />
                  {selectedPacker && (
                    <button onClick={() => { setSelectedPacker(''); setPackerHistory(null); setPackerSearch(''); }}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-lg font-bold">×</button>
                  )}
                </div>
                {packerSearch && !selectedPacker && (
                  <div className="border-2 border-orange-100 rounded-xl mt-1 max-h-48 overflow-y-auto shadow-lg bg-white z-10 relative">
                    {data.packagerStats
                      .filter(p => p.packager_name.toLowerCase().includes(packerSearch.toLowerCase()) || p.packager_number.toLowerCase().includes(packerSearch.toLowerCase()))
                      .map((p, i) => (
                        <button key={i} onClick={() => { handlePackerSelect(p.packager_number); setPackerSearch(p.packager_name + ' (' + p.packager_number + ')'); }}
                          className="w-full text-left px-4 py-3 hover:bg-orange-50 border-b border-gray-50 last:border-0 transition-colors">
                          <span className="font-semibold text-gray-800">{p.packager_name}</span>
                          <span className="text-gray-400 text-xs ml-2">{p.packager_number}</span>
                          <span className={`float-right text-xs font-bold px-2 py-1 rounded-full ${parseFloat(p.avg_total_pct) <= 3 ? 'bg-green-100 text-green-700' : parseFloat(p.avg_total_pct) <= 5 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>
                            {parseFloat(p.avg_total_pct).toFixed(2)}%
                          </span>
                        </button>
                      ))}
                  </div>
                )}
              </div>

              {packerHistoryLoading && (
                <div className="text-center py-8">
                  <div className="text-4xl fruit-spin inline-block">🍊</div>
                </div>
              )}

              {!packerHistoryLoading && packerHistory && (
                <>
                  {/* Packer summary row */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                    {[
                      { label: t('boxes'), value: packerHistory.packager.overall.boxes_inspected, icon: '📦', color: 'bg-orange-50 border-orange-200' },
                      { label: t('avg_minor'), value: parseFloat(packerHistory.packager.overall.avg_minor_pct).toFixed(2) + '%', icon: '⚡', color: 'bg-yellow-50 border-yellow-200' },
                      { label: t('avg_major'), value: parseFloat(packerHistory.packager.overall.avg_major_pct).toFixed(2) + '%', icon: '🚨', color: 'bg-red-50 border-red-200' },
                      { label: t('total_pct'), value: parseFloat(packerHistory.packager.overall.avg_total_pct).toFixed(2) + '%', icon: '📊', color: 'bg-blue-50 border-blue-200' },
                    ].map((card, i) => (
                      <div key={i} className={`rounded-xl p-3 border-2 ${card.color}`}>
                        <div className="text-xl mb-1">{card.icon}</div>
                        <div className="text-lg font-extrabold text-gray-800">{card.value}</div>
                        <div className="text-xs font-semibold text-gray-500">{card.label}</div>
                      </div>
                    ))}
                  </div>

                  {/* Daily trend chart */}
                  {packerLineData && (
                    <div className="mb-5">
                      <h4 className="text-sm font-bold text-gray-600 mb-2">📈 {t('daily_performance')}</h4>
                      <Line data={packerLineData} options={{
                        responsive: true, interaction: { mode: 'index', intersect: false },
                        scales: {
                          y: { beginAtZero: true, title: { display: true, text: 'Defect %' } },
                          y1: { beginAtZero: true, position: 'right', title: { display: true, text: t('boxes') }, grid: { drawOnChartArea: false } }
                        }
                      }} />
                    </div>
                  )}

                  {/* Daily history table */}
                  <div className="touch-scroll">
                    <table className="w-full text-sm" style={{ minWidth: '600px' }}>
                      <thead>
                        <tr className="border-b-2 border-orange-100">
                          <th className="text-left py-3 px-3 font-bold text-gray-600">{t('from_date')}</th>
                          <th className="text-center py-3 px-3 font-bold text-gray-600">{t('boxes')}</th>
                          <th className="text-center py-3 px-3 font-bold text-yellow-600">{t('avg_minor')}</th>
                          <th className="text-center py-3 px-3 font-bold text-red-600">{t('avg_major')}</th>
                          <th className="text-center py-3 px-3 font-bold text-gray-600">{t('total_pct')}</th>
                          <th className="text-center py-3 px-3 font-bold text-gray-600">{t('rating')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {packerHistory.dailyHistory.map((d, i) => {
                          const total = parseFloat(d.avg_total_pct);
                          return (
                            <tr key={i} className="border-b border-gray-50 hover:bg-orange-50 transition-colors">
                              <td className="py-3 px-3 font-semibold">{new Date(d.date).toLocaleDateString()}</td>
                              <td className="py-3 px-3 text-center font-bold">{d.boxes_inspected}</td>
                              <td className="py-3 px-3 text-center"><span className="px-2 py-1 rounded-full text-xs font-bold bg-yellow-100 text-yellow-700">{parseFloat(d.avg_minor_pct).toFixed(2)}%</span></td>
                              <td className="py-3 px-3 text-center"><span className={`px-2 py-1 rounded-full text-xs font-bold ${parseFloat(d.avg_major_pct) > 5 ? 'bg-red-100 text-red-700' : parseFloat(d.avg_major_pct) > 2 ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'}`}>{parseFloat(d.avg_major_pct).toFixed(2)}%</span></td>
                              <td className="py-3 px-3 text-center font-bold">{total.toFixed(2)}%</td>
                              <td className="py-3 px-3 text-center text-lg">{total <= 3 ? '🌟' : total <= 5 ? '👍' : total <= 10 ? '⚠️' : '🚨'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {!packerHistoryLoading && !packerHistory && !selectedPacker && (
                <div className="text-center py-8 text-gray-400">
                  <div className="text-4xl mb-2">📦</div>
                  <div className="text-sm">{t('packer_history_prompt')}</div>
                </div>
              )}
            </div>

            {/* Inspector Performance */}
            <div id="section-inspectors"></div>
            <div className="bg-white rounded-2xl shadow-md p-5 border border-orange-100">
              <h3 className="text-base font-bold text-gray-800 mb-3">👷 {t('inspector_performance')}</h3>
              <div className="touch-scroll">
                <table className="w-full text-sm" style={{ minWidth: '600px' }}>
                  <thead>
                    <tr className="border-b-2 border-orange-100">
                      <th className="text-left py-3 px-3 font-bold text-gray-600">{t('inspector')}</th>
                      <th className="text-center py-3 px-3 font-bold text-gray-600">{t('boxes')}</th>
                      <th className="text-center py-3 px-3 font-bold text-yellow-600">{t('avg_minor')}</th>
                      <th className="text-center py-3 px-3 font-bold text-red-600">{t('avg_major')}</th>
                      <th className="text-center py-3 px-3 font-bold text-purple-600">{t('avg_critical')}</th>
                      <th className="text-center py-3 px-3 font-bold text-gray-600">{t('minor_found')}</th>
                      <th className="text-center py-3 px-3 font-bold text-gray-600">{t('major_found')}</th>
                      <th className="text-center py-3 px-3 font-bold text-gray-600">{t('critical_found')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.inspectorStats.map((s, i) => (
                      <tr key={i} className="border-b border-gray-50 hover:bg-orange-50 transition-colors">
                        <td className="py-3 px-3 font-semibold">{s.username}</td>
                        <td className="py-3 px-3 text-center font-bold">{s.boxes_inspected}</td>
                        <td className="py-3 px-3 text-center"><span className="px-2 py-1 rounded-full text-xs font-bold bg-yellow-100 text-yellow-700">{parseFloat(s.avg_minor_pct).toFixed(2)}%</span></td>
                        <td className="py-3 px-3 text-center"><span className={`px-2 py-1 rounded-full text-xs font-bold ${parseFloat(s.avg_major_pct) > 5 ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>{parseFloat(s.avg_major_pct).toFixed(2)}%</span></td>
                        <td className="py-3 px-3 text-center"><span className={`px-2 py-1 rounded-full text-xs font-bold ${parseFloat(s.avg_critical_pct) > 0 ? 'bg-purple-100 text-purple-700' : 'bg-green-100 text-green-700'}`}>{parseFloat(s.avg_critical_pct).toFixed(2)}%</span></td>
                        <td className="py-3 px-3 text-center">{s.total_minor}</td>
                        <td className="py-3 px-3 text-center">{s.total_major}</td>
                        <td className="py-3 px-3 text-center font-bold text-purple-700">{s.total_critical}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
